/**
 * Default formatting providers backed by @entrata/i18n.
 *
 * Each provider is a pure factory function that receives an I18nAdapter
 * (Dependency Inversion). None of them touch i18nUtils directly — that is
 * i18nAdapter.ts's sole responsibility.
 *
 * createDefaultFormattingRegistry(adapter) is the main entry point.
 * Pass a mock adapter in tests; omit it in production to get the global one.
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { SUPPORTED_LOCALES } from 'utils/locale';

import {
  fallbackParseToIso,
  formatDateInputAsYouType,
  formatIsoDateWithIntl,
  getLocaleDateInputPipeFormat,
  isoToDisplay,
  normalizeToIsoDate,
  parseLocaleDateInputToIso,
} from './dateInputUtils';
import {
  getFormattingCurrencyCode,
  getFormattingDateLocale,
  getFormattingLocale,
  getFormattingSettings,
} from './formattingLocale';
import { globalI18nAdapter, type I18nAdapter } from './i18nAdapter';
import type {
  CurrencyFormatter,
  DateFormatter,
  DateInputFormattingProvider,
  FormattingRegistry,
  LocaleProvider,
  NumberFormatter,
  PhoneFormattingProvider,
} from './types';

function createLocaleProvider(i18n: I18nAdapter): LocaleProvider {
  return {
    getLocale(): string {
      return (
        i18n.getLocale() ??
        (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US')
      );
    },
  };
}

function getConfiguredGroupSize(grouping: string | undefined): number | undefined {
  if (!grouping || grouping === '0') return undefined;

  const groupSize = Number(grouping);
  return Number.isInteger(groupSize) && groupSize > 0 && groupSize <= 20 ? groupSize : undefined;
}

function getIntlGroupingOption(grouping: string | undefined): boolean | undefined {
  return grouping === '0' || getConfiguredGroupSize(grouping) ? false : undefined;
}

function applyConfiguredGrouping(
  parts: Intl.NumberFormatPart[],
  locale: string,
  grouping: string | undefined,
  thousandsSeparator: string | undefined,
): Intl.NumberFormatPart[] {
  const groupSize = getConfiguredGroupSize(grouping);
  if (!groupSize) return parts;

  const groupSeparator =
    thousandsSeparator ??
    new Intl.NumberFormat(locale).formatToParts(1000).find((part) => part.type === 'group')?.value;
  if (!groupSeparator) return parts;

  return parts.flatMap((part) => {
    if (part.type !== 'integer' || part.value.length <= groupSize) return [part];

    const digits = Array.from(part.value);
    const firstGroupLength = digits.length % groupSize || groupSize;
    const groups = [digits.slice(0, firstGroupLength).join('')];
    for (let index = firstGroupLength; index < digits.length; index += groupSize) {
      groups.push(digits.slice(index, index + groupSize).join(''));
    }

    return groups.flatMap((group, index) => [
      ...(index > 0 ? [{ type: 'group' as const, value: groupSeparator }] : []),
      { type: 'integer' as const, value: group },
    ]);
  });
}

function createCurrencyFormatter(i18n: I18nAdapter, locale: LocaleProvider): CurrencyFormatter {
  return {
    format(amount: number, currencyCode?: string): string {
      const formattingLocale = getFormattingLocale();
      const settings = getFormattingSettings();
      const configuredDigits = settings?.currencyDigits;
      const currencyDigits =
        typeof configuredDigits === 'number' &&
        Number.isInteger(configuredDigits) &&
        configuredDigits >= 0 &&
        configuredDigits <= 20
          ? configuredDigits
          : undefined;
      if (formattingLocale) {
        try {
          const effectiveCurrencyCode =
            settings?.currencyCode ?? currencyCode ?? getFormattingCurrencyCode();
          const parts = applyConfiguredGrouping(
            new Intl.NumberFormat(formattingLocale, {
              ...(effectiveCurrencyCode
                ? { style: 'currency', currency: effectiveCurrencyCode }
                : {}),
              ...(currencyDigits !== undefined
                ? { minimumFractionDigits: currencyDigits, maximumFractionDigits: currencyDigits }
                : {}),
              useGrouping: getIntlGroupingOption(settings?.numberFormat?.grouping),
            }).formatToParts(amount),
            formattingLocale,
            settings?.numberFormat?.grouping,
            settings?.numberFormat?.thousandsSeparator,
          );
          const formatted = parts
            .map((part) => {
              if (part.type === 'currency' && settings?.currencySymbol) {
                return settings.currencySymbol;
              }
              if (part.type === 'decimal' && settings?.numberFormat?.decimalSeparator) {
                return settings.numberFormat.decimalSeparator;
              }
              if (part.type === 'group' && settings?.numberFormat?.thousandsSeparator) {
                return settings.numberFormat.thousandsSeparator;
              }
              return part.value;
            })
            .join('');
          return !effectiveCurrencyCode && settings?.currencySymbol
            ? `${settings.currencySymbol}${formatted}`
            : formatted;
        } catch {
          return amount.toFixed(currencyDigits ?? 2);
        }
      }

      if (!currencyCode) {
        const result = i18n.currency(amount, 'p:2');
        if (result !== undefined) return result;
        return amount.toFixed(2);
      }
      try {
        return new Intl.NumberFormat(locale.getLocale(), {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return amount.toFixed(2);
      }
    },
  };
}

function createNumberFormatter(i18n: I18nAdapter, locale: LocaleProvider): NumberFormatter {
  return {
    format(value: number | string, options?: Intl.NumberFormatOptions): string {
      const formattingLocale = getFormattingLocale();
      const settings = getFormattingSettings();
      if (formattingLocale) {
        try {
          return applyConfiguredGrouping(
            new Intl.NumberFormat(formattingLocale, {
              useGrouping: getIntlGroupingOption(settings?.numberFormat?.grouping),
              ...options,
            }).formatToParts(Number(value)),
            formattingLocale,
            settings?.numberFormat?.grouping,
            settings?.numberFormat?.thousandsSeparator,
          )
            .map((part) => {
              if (part.type === 'decimal' && settings?.numberFormat?.decimalSeparator) {
                return settings.numberFormat.decimalSeparator;
              }
              if (part.type === 'group' && settings?.numberFormat?.thousandsSeparator) {
                return settings.numberFormat.thousandsSeparator;
              }
              return part.value;
            })
            .join('');
        } catch {
          return String(value);
        }
      }

      if (options) {
        return new Intl.NumberFormat(locale.getLocale(), options).format(Number(value));
      }

      const result = i18n.number(value, 'p:2');
      if (result !== undefined) return result;
      return new Intl.NumberFormat(locale.getLocale(), options).format(Number(value));
    },
  };
}

function createDateFormatter(i18n: I18nAdapter, locale: LocaleProvider): DateFormatter {
  function toDate(date: Date | string): Date {
    if (typeof date !== 'string') return date;

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    return new Date(date);
  }

  function formatWithIntl(
    date: Date,
    options: Intl.DateTimeFormatOptions,
    localeCode = getFormattingDateLocale() ?? locale.getLocale(),
  ): string {
    try {
      return new Intl.DateTimeFormat(localeCode, options).format(date);
    } catch {
      return new Intl.DateTimeFormat('en-US', options).format(date);
    }
  }

  return {
    formatDate(date: Date | string, formatKey = 'DATE_ALPHA_SHORT'): string {
      const d = toDate(date);
      if (Number.isNaN(d.getTime())) return 'Invalid date';
      if (getFormattingDateLocale()) {
        return formatWithIntl(d, { year: 'numeric', month: 'short', day: 'numeric' });
      }
      return (
        i18n.datetime(d, formatKey) ??
        formatWithIntl(d, { year: 'numeric', month: 'short', day: 'numeric' })
      );
    },

    formatDateLong(date: Date | string): string {
      const d = toDate(date);
      if (Number.isNaN(d.getTime())) return 'Invalid date';
      if (getFormattingDateLocale()) {
        return formatWithIntl(d, { year: 'numeric', month: 'long', day: 'numeric' });
      }
      return (
        i18n.datetime(d, 'DATE_ALPHA_LONG') ??
        formatWithIntl(d, { year: 'numeric', month: 'long', day: 'numeric' })
      );
    },

    formatDateTime(date: Date | string, options: Intl.DateTimeFormatOptions = {}): string {
      const d = toDate(date);
      if (Number.isNaN(d.getTime())) return 'Invalid date';
      return formatWithIntl(d, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...options,
      });
    },
  };
}

function createPhoneProvider(i18n: I18nAdapter): PhoneFormattingProvider {
  function isValidCode(code: number): boolean {
    return i18n.isValidCountryCode(code);
  }

  return {
    getCountryCode(): string {
      const code = i18n.getCountryCode();
      return code != null ? String(code) : '1';
    },

    isValidCountryCode: isValidCode,

    formatDisplay(digits: string, countryCode: string): string | false {
      const ccNum = parseInt(countryCode, 10);
      if (!isValidCode(ccNum)) return false;
      const format = i18n.getPhoneFormat();
      if (!format) return false;

      // @entrata/i18n uses libphonenumber-js which logs to console.error when
      // parsing very short strings. To prevent console noise and error notifications
      // we'll bypass the formatter for strings under 2 digits.
      if (digits.length < 2) return false;

      try {
        const result = format(digits, ccNum);
        if (result && typeof result.formattedPhoneNumber === 'string') {
          return result.formattedPhoneNumber;
        }
      } catch {
        // fall through to false
      }
      return false;
    },

    formatE164(e164: string): string | null {
      try {
        const parsed = parsePhoneNumberFromString(e164);
        if (parsed) {
          const formatted = `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
          return i18n.phone(formatted) ?? null;
        }
      } catch {
        // fall through
      }
      return null;
    },

    getPlaceholder(countryCode: string): string {
      const ccNum = parseInt(countryCode, 10);
      if (isValidCode(ccNum)) {
        try {
          const utils = i18n.getPhoneUtils(ccNum);
          if (utils?.getPhonePlaceholder) return utils.getPhonePlaceholder();
        } catch {
          // fall through
        }
      }
      return '(000) 000 - 0000';
    },
  };
}

function createDateInputProvider(i18n: I18nAdapter): DateInputFormattingProvider {
  function hasI18nDateInputFormat(locale: string): boolean {
    return SUPPORTED_LOCALES.includes(
      locale.replace(/_/g, '-') as (typeof SUPPORTED_LOCALES)[number],
    );
  }

  function getPipeFormat(locale: string): string {
    const utils = i18n.getDateUtils({ locale });
    return utils?.pipeFormat && hasI18nDateInputFormat(locale)
      ? utils.pipeFormat
      : getLocaleDateInputPipeFormat(locale);
  }

  return {
    getPlaceholder(locale: string): string {
      const utils = i18n.getDateUtils({ locale });
      return utils?.getPlaceholder?.() && hasI18nDateInputFormat(locale)
        ? utils.getPlaceholder()
        : getPipeFormat(locale);
    },

    formatForDisplay(isoValue: string, locale: string): string {
      const utils = i18n.getDateUtils({ locale });
      return utils?.pipeFormat && hasI18nDateInputFormat(locale)
        ? isoToDisplay(isoValue, utils.pipeFormat)
        : formatIsoDateWithIntl(isoValue, locale);
    },

    formatAsYouType(partialValue: string, locale: string): string {
      return formatDateInputAsYouType(partialValue, getPipeFormat(locale));
    },

    parseToIso(displayValue: string, locale: string): string {
      const utils = i18n.getDateUtils({ locale });
      if (utils?.getRawValue && hasI18nDateInputFormat(locale)) {
        return normalizeToIsoDate(utils.getRawValue(displayValue));
      }
      const parsedLocaleDate = parseLocaleDateInputToIso(displayValue, getPipeFormat(locale));
      return normalizeToIsoDate(
        parsedLocaleDate === displayValue ? fallbackParseToIso(displayValue) : parsedLocaleDate,
      );
    },
  };
}

/**
 * Creates a FormattingRegistry backed by the given adapter.
 * Production code uses the default (globalI18nAdapter).
 * Tests pass a mock adapter to avoid touching the i18nUtils global.
 */
export function createDefaultFormattingRegistry(
  adapter: I18nAdapter = globalI18nAdapter,
): FormattingRegistry {
  const locale = createLocaleProvider(adapter);
  return {
    locale,
    currency: createCurrencyFormatter(adapter, locale),
    number: createNumberFormatter(adapter, locale),
    date: createDateFormatter(adapter, locale),
    phone: createPhoneProvider(adapter),
    dateInput: createDateInputProvider(adapter),
  };
}

/** Default registry using the production @entrata/i18n adapter. */
export const defaultFormattingRegistry: FormattingRegistry = createDefaultFormattingRegistry();
